import React, { useState, useEffect } from 'react';
import PropTypes, {
  shape,
  string,
  arrayOf,
  func,
} from 'prop-types';
import { CarouselProvider } from 'pure-react-carousel';
import { shuffleArray } from '../AmexGDAB2BDetails';
import AmexGDAB2BTestimonialsCard from './AmexGDAB2BTestimonialsCard';
import AmexGDAB2BTestimonialsCarousel from './AmexGDAB2BTestimonialsCarousel';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexGDAB2BTestimonials.scss';

const AmexGDAB2BTestimonials = (
  {
    baseRequestUrl,
    getTestimonialsComponentStatus,
    matchMedia,
    testimonialsData: {
      content = [],
      title,
      copy,
      section_id: sectionId,
      sr_title: srTitle,
      sr_info: srInfo,
      sr_controls_aria: srControlsAria,
    },
  }
) => {
  if (!title || content.length === 0) {
    getTestimonialsComponentStatus(false);
    return null;
  }
  const imagePathPrefix = `${baseRequestUrl}AmexGDAB2B/images/Testimonials/`;
  const visibleSlides = 5;
  const requiredKeys = ['bg_image', 'name', 'text'];
  const validatedTestimonialsContent = content.slice()
    .filter((item) => requiredKeys.every((i) => Object.prototype.hasOwnProperty.call(item, i) && item[i]));
  const [randomTestimonials, setRandomTestimonials] = useState([]);

  if (validatedTestimonialsContent.length === 0) {
    getTestimonialsComponentStatus(false);
    return null;
  }

  useEffect(() => {
    const shuffle = shuffleArray(validatedTestimonialsContent);
    setRandomTestimonials(shuffle);
  }, [content]);

  const renderTestimonials = () => (
    <ul className="AmexGDAB2BTestimonials__list">
      {randomTestimonials.map((item, index) => (
        <li
          key={item.text}
          className="AmexGDAB2BTestimonialsCard"
          tabIndex="0" // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
        >
          <AmexGDAB2BTestimonialsCard
            imagePathPrefix={imagePathPrefix}
            index={index}
            {...item}
          />
        </li>
      ))}
    </ul>
  );

  const sanitizedTitle = sanitizeString(title);
  const sanitizedCopy = sanitizeString(copy);

  return (
    <section className="AmexGDAB2BTestimonials">
      <div className="container">
        <h2
          id={sectionId}
          className="AmexGDAB2BTestimonials__title"
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: sanitizedTitle,
          }}
        />
        {copy && (
          <p
            className="AmexGDAB2BTestimonials__text"
            dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
              __html: sanitizedCopy,
            }}
          />
        )}
        <div className="AmexGDAB2BTestimonials__wrapper">
          {(matchMedia.xl && randomTestimonials.length > 5) ? (
            <CarouselProvider
              isIntrinsicHeight
              naturalSlideWidth={400}
              naturalSlideHeight={650}
              totalSlides={randomTestimonials.length}
              dragEnabled={false}
              step={5}
              visibleSlides={visibleSlides}
            >
              <AmexGDAB2BTestimonialsCarousel
                testimonials={randomTestimonials}
                imagePathPrefix={imagePathPrefix}
                srInfo={srInfo}
                srControlsAria={srControlsAria}
                srTitle={srTitle}
              />
            </CarouselProvider>
          ) : renderTestimonials()}
        </div>
      </div>
    </section>
  );
};

AmexGDAB2BTestimonials.propTypes = {
  baseRequestUrl: string.isRequired,
  getTestimonialsComponentStatus: func.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    ipadLandscape: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  testimonialsData: shape({
    title: string,
    copy: string,
    content: arrayOf(shape({})),
    section_id: string,
  }).isRequired,
};

export default AmexGDAB2BTestimonials;
